import { logger } from '../../utils/logger'
/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, Inject, OnInit, ViewChild, DestroyRef, inject } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { PermitirAlteracaoService } from 'src/app/services/permitir-alteracao.service'
import { Campus } from 'src/app/models/campus'
import { Concurso } from 'src/app/models/concurso'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { RegistroPermitirAlteracao } from 'src/app/models/registro-permitir-alteracao'
import { TipoRegistro } from 'src/app/enums/tipo-registro'
import { map, Observable, of, startWith } from 'rxjs'
import { UnespCoreEnvironment, UnespCoreMessageService } from 'src/libs/unesp-core'
import { ProvaEtapa, TipoProva } from 'src/app/enums/tipo-prova'
import { ConfirmDialogModel, ConfirmDialogComponent } from 'src/app/modules/confirm-dialog/confirm-dialog.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-permitir-alteracao',
  templateUrl: './permitir-alteracao.component.html',
  styleUrls: ['./permitir-alteracao.component.scss'],
})
export class PermitirAlteracaoComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild(MatPaginator) paginator!: MatPaginator

  colunasGrid: string[] = ['id']
  dataSource: MatTableDataSource<RegistroPermitirAlteracao> = new MatTableDataSource()

  tipoRegistroFC: FormControl = new FormControl('')
  unidadeFC: FormControl = new FormControl('')
  concursoFC: FormControl = new FormControl('')

  tipoRegistro = TipoRegistro

  tipoProva: any = TipoProva
  provaEtapa: any = ProvaEtapa

  unidades: Campus[] = []
  concursos: Concurso[] = []

  unidadesFiltradas: Observable<Campus[]> = of(this.unidades)
  concursosFiltrados: Observable<Concurso[]> = of(this.concursos)

  private baseUrl: string

  constructor(
    @Inject('environment') env: UnespCoreEnvironment,
    private readonly permitirAlteracaoService: PermitirAlteracaoService,
    public dialog: MatDialog,
    private unespCoreMessageService: UnespCoreMessageService
  ) {
    this.baseUrl = env.baseUrlRedirect.replace('/auth/', '')
  }

  ngOnInit(): void {
    this.unidadeFC.disable()
    this.concursoFC.disable()

    this.permitirAlteracaoService.getUnidades().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(arr => {
      this.unidades = arr;
      if (arr.length === 1) {
        this.unidadeFC.setValue(arr[0]);
        this.unidadeFC.disable();
      } else {
        this.unidadeFC.enable();
      }
    });

    this.tipoRegistroFC.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(opt => this.mostrarUnidades())

    this.unidadesFiltradas = this.unidadeFC.valueChanges.pipe(
      startWith(''),
      map(value => {
        const nome = typeof value === 'string' ? value : this.getConcursos(value)
        return nome ? this._filtroUnidade(nome as string) : this.unidades.slice()
      })
    )

    this.concursosFiltrados = this.concursoFC.valueChanges.pipe(
      startWith(''),
      map(value => {
        const nome = typeof value === 'string' ? value : this.listar(value)
        return nome ? this._filtroConcurso(nome as string) : this.concursos.slice()
      })
    )

    this.dataSource.paginator = this.paginator
  }

  private _filtroUnidade(nome: string) {
    const filterValue = nome.toLowerCase()
    return this.unidades.filter(opt => opt.nome.toLowerCase().includes(filterValue))
  }

  nomeUnidade(unidade: Campus): string {
    return unidade && unidade.nome ? unidade.nome : ''
  }

  mostrarUnidades() {
    if (this.unidades && this.unidades.length > 1) {
      this.unidadeFC.reset();
      this.unidadeFC.enable();
    }
    this.concursoFC.reset()    
  }

  private _filtroConcurso(nome: string) {
    const filterValue = nome.toLowerCase()
    return this.concursos.filter(opt => opt.titulo.toLowerCase().includes(filterValue))
  }

  tituloConcurso(concurso: Concurso): string {
    return concurso && concurso.titulo ? concurso.titulo : ''
  }

  getConcursos(unidade: Campus) {
    if (unidade == null) return
    this.permitirAlteracaoService.getConcursos(unidade.uuid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(arr => {
      this.concursos = arr
      this.concursoFC.reset()
      this.concursoFC.enable()
    })
  }

  listar(concurso: Concurso) {
    if (concurso != null) {
      const id = concurso.id as number
      if (id === undefined) return
      const tipo = this.tipoRegistroFC.value
      switch (tipo) {
        case 'DEFERIMENTO':
          this.colunasGrid = ['id', 'dados_inscricao', 'deferimento', 'action']
          break
        case 'PAGAMENTO':
          this.colunasGrid = ['id', 'dados_inscricao', 'pagamento', 'action']
          break
        case 'REDUCAO':
          this.colunasGrid = ['id', 'dados_inscricao', 'reducao', 'action']
          break
        case 'ANDAMENTO':
          this.colunasGrid = ['id', 'andamento_1', 'andamento_2', 'action']
          break
        case 'RECURSO':
          this.colunasGrid = ['id', 'recurso_0', 'recurso_1', 'recurso_2', 'action']
          break
        case 'CONCURSOPROVA':
          this.colunasGrid = ['prova_0', 'prova_1', 'prova_action']
          break
        case 'CLASSIFICACAO':
          this.colunasGrid = ['id', 'dados_inscricao', 'classificacao_action']
          break
        default:
          return
      }
      this.permitirAlteracaoService
        .listarRegistros(tipo.toLowerCase(), id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (registros) => {
            this.dataSource = new MatTableDataSource(registros)
          },
          error: (erro) => {            
            this.unespCoreMessageService.showMessageError(erro.error.message)
          }
        })
    }
  }

  abrirNovaAba(tipo: string, id: number): void {
    window.open(`${this.baseUrl}/permitir-alteracao/${tipo}/${id}`, '_blank')
  }

  abrirNovaRecurso(id: number): void {
    window.open(`${this.baseUrl}/recurso/analise/${id}`, '_blank')
  }

  mudarParaEdicao(registro: RegistroPermitirAlteracao) {
    const prova = registro.concursoProva

    const provaNome = (TipoProva as any)[prova.tipoProva].nome

    const alteracao = (prova.provaEtapa as string) == 'CONCLUIDA' ? 'não concluída e não publicada' : 'não publicada'

    const confirmDialog = new ConfirmDialogModel('Atenção', `Mudar ${provaNome} para ${alteracao}?`)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: confirmDialog,
    })
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res)
        this.permitirAlteracaoService.reverterProva(registro.id, prova.tipoProva, 'EDICAO').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
           this.listar(this.concursoFC.value)
          },
          error: (erro) => {
            this.unespCoreMessageService.showMessageError(erro.error.message)
          }
        })
    })
  }

  mudarParaPublicada(registro: RegistroPermitirAlteracao) {
    const prova = registro.concursoProva

    const provaNome = (TipoProva as any)[prova.tipoProva].nome

    const confirmDialog = new ConfirmDialogModel('Atenção', `Mudar ${provaNome} para não concluída?`)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: confirmDialog,
    })
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res)
        this.permitirAlteracaoService.reverterProva(registro.id, prova.tipoProva, 'PUBLICADA').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.listar(this.concursoFC.value)
          },
          error: (erro) => {
            this.unespCoreMessageService.showMessageError(erro.error.message)
          }
        })
    })
  }

  habilitarClassificacao(inscricaoId: number) {
    const confirmDialog = new ConfirmDialogModel('Atenção', 'Habilitar classificação para esta inscrição?')
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: confirmDialog,
    })
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res)
        this.permitirAlteracaoService.habilitarClassificacao(inscricaoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.listar(this.concursoFC.value)
          },
          error: (erro) => {
            logger.log('Atenção', erro)
            this.unespCoreMessageService.showMessageError(erro.error.message)
          }
        })
    })
  }
}
